import wixData from 'wix-data'
import wixWindow from 'wix-window'
import wixLocationFrontend from 'wix-location-frontend'
import wixSeoFrontend from 'wix-seo-frontend'
import wixSiteFrontend from 'wix-site-frontend'

import { getAuthorSobdomains, getDictionary } from '@wix-platform/blog-cross-posts-backend'
import { POSTS_COLLECTION_ID, AUTHORS_COLLECTION_ID, AUTHOR_URL_PATH } from '../public/settings'
import { createSeoDescription, slugifyUrl, linkText, getPreviousPage, getImageData, isValidHttpUrl } from '../public/utils'

const POSTS_PER_PAGE = 12

let dictionary = {}

export async function initMembers($containerElement = {}) {
    const [_dictionary, author] = await Promise.all([
        getDictionary(),
        getAuthor()
    ])

    if (!author) {
        $w('#authorProfileP1, #authorProfileP2, #postsSection').delete()
        console.error('Did not find author')
        console.log('wixSeoFrontend.metaTags', wixSeoFrontend.metaTags)
        return wixSeoFrontend.setMetaTags([ ...wixSeoFrontend.metaTags, { name: 'robots', content: 'noindex' }])
        // return wixSeoFrontend.setSeoStatusCode(404)
    }

    if ($containerElement.delete) $containerElement.delete()

    dictionary = _dictionary

    setPageTitle(author)
    await Promise.all([
        renderAuthorInfo(author),
        handlePosts(author),
        setAuthorCanonical(author.canonical),
        setMetaTags(author)
        // setAuthorStructuredData(author)
    ])
    backButton()
}

const createPageTitle = (author) => {
    const { name, title } = author
    return (title) ? `${name}, ${title}` : name
}

const setPageTitle = (author) => {
    $w('#pagination').customPageTitleTemplate = `${createPageTitle(author)}{page}`
}

const getAuthor = async () => {
    const [, memberSlug] = wixLocationFrontend.path
    const member = await wixData.query('Members/PublicData')
        .eq('slug', memberSlug)
        .find()
        .then(({ items }) => items[0])

    if (!member) return null

    const authorId = slugifyUrl(member.nickname)
    let author = await wixData.get(AUTHORS_COLLECTION_ID, authorId)
    if (!author) author = await wixData.get(AUTHORS_COLLECTION_ID, memberSlug)

    return author
}

const renderAuthorInfo = async (author) => {
    const {
        name,
        description,
        profilePhoto,
        website,
        subDomains,
        linkedinLink,
        twitterLink,
        facebookLink,
        instagramLink,
        tiktokLink,
        youtubeLink
    } = author

    // $w('#name').text = name // moved to handlePosts becuase it contains the page
    $w('#description').text = description
    $w('#profilePhoto, #profilePhotoP2').src = profilePhoto
    $w('#profilePhoto, #profilePhotoP2').alt = name

    if (website) {
        $w('#website').text = website
        $w('#website').expand()
    } else {
        $w('#website').delete()
    }

    const socialLinks = [linkedinLink, twitterLink, facebookLink, instagramLink, tiktokLink, youtubeLink]

    $w('#socialBar').forEachItem(($item, itemData, index) => {
        const socialLink = socialLinks[index]
        if (socialLink) {
            $item('#socialContainer').expand()
            $item('#socialButton').link = socialLink
        } else {
            $item('#socialContainer').collapse()
        }
    })

    const { firstName } = splitName(name)
    const $postsTitle = $w('#postsTitle')
    $postsTitle.text = $postsTitle.text.replace('{firstName}', firstName)

    const isFrench = (wixSiteFrontend.language === 'fr')
    const nameStartsWithVowel = /^[aeiouyàâäéèêëîïôöùûüÿ]/i.test(firstName[0])
    if (isFrench && nameStartsWithVowel) $postsTitle.text = $postsTitle.text.replace('de ', 'd\'')

    await otherBlogsList(author, subDomains)
}

const otherBlogsList = async (author, subDomainKeys) => {
    if (!subDomainKeys) return

    const $otherBlogsList = $w('#otherBlogsList')

    const subDomains = await getAuthorSobdomains(subDomainKeys)
    let subDomainsText = ''
    subDomains.forEach((subDomain, index) => {
        const { name, url } = subDomain

        const isLast = (subDomains.length === index + 1)
        const isFirst = (index === 0)
        if (!isLast && !isFirst) subDomainsText += ', '
        if (isLast && !isFirst) subDomainsText += dictionary[' and ']

        const authorLink = `${url}/profile/${author._id}/${AUTHOR_URL_PATH}`
        const underlineStyle = 'text-decoration:underline'
        subDomainsText += `<a style="${underlineStyle}" href="${authorLink}">${name}</a>`
    })

    const variables = {
        authorName: author.name,
        blogsList: subDomainsText
    }
    updateTemplatedElement($otherBlogsList, 'html', variables)

    $otherBlogsList.expand()
}

const handlePosts = async (author) => {
    const $pagination = $w('#pagination')

    $w('#posts').onItemReady(renderPost)
    const currentPage = $pagination.getCurrentPage()
    const postsResults = await getPosts(author._id, currentPage)
    if (!postsResults.length) {
        $w('#emptyState').expand()
        $w('#postsInfo, #posts, #pagination').delete()
    } else {
        $w('#emptyState').delete()
    }
    updatePosts(postsResults)
    await handleStaticContent(currentPage)
    updateName(author.name, currentPage)
    truncatePostsTitles()

    if (postsResults.totalPages <= 1) {
        $pagination.delete()
    } else {
        $pagination.totalPages = postsResults.totalPages
        $pagination.onChange(async event => {
            const { page } = event.data
            const postsResults = await getPosts(author._id, page)
            updatePosts(postsResults)
            handleStaticContent(page)
            updateName(author.name, page)
        })
    }
}

const getPosts = (authorId, page) => {
    const skip = (page - 1) * POSTS_PER_PAGE

    return wixData.query(POSTS_COLLECTION_ID)
        .hasSome('authors', authorId)
        .include('blog')
        .descending('publishDate')
        .limit(POSTS_PER_PAGE)
        .skip(skip)
        .find()
}

const renderPost = ($item, post) => {
    const { image, title, slug, blog } = post

    const $postImage = $item('#postImage')
    const $postCategory = $item('#postCategory')
    const $postTitle = $item('#postTitle')
    
    const slashFiller = (blog.postsUrl.slice(-1) === '/') ? '' : '/'
    const postUrl = `${blog.postsUrl}${slashFiller}${slug}`

    $postImage.src = image
    $postImage.alt = title
    $postImage.link = postUrl
    $postImage.target = '_self'
    $postCategory.label = blog.name.toUpperCase()
    $postCategory.target = '_self'
    $postCategory.link = blog.url
    $postTitle.text = title
    linkText($postTitle, postUrl)

    handleFrenchSpace($postTitle)
}

const updatePosts = (postsResults) => {
    $w('#posts').data = postsResults.items

    const isSinglePost = (postsResults.totalCount === 1)
    const templateText = dictionary[(isSinglePost ? '{{count}} article' : '{{count}} articles')]
    $w('#postsCount').text = templateText.replace(`{{count}}`, postsResults.totalCount)
}

const handleStaticContent = async (page) => {
    const $staticContent = $w('#description, #website, #socialBar')
    if (page > 1) {
        return $staticContent.delete()
    } else {
        const restorePromisses = $staticContent.map($element => {
            return ($element.collapsed) ? null : $element.restore()
        })
        return Promise.all(restorePromisses)
        // return $staticContent.restore()
    }
}

// In French all forms of "high" punctuation have a space preceding them,
// we replace that space with a &nbsp; so it won't cause an ugly line wrap.
const handleFrenchSpace = ($element) => {
    const text = $element.html.replace(/(<([^>]+)>)/g, '') // strip HTML to get only the text
    const textWithFrenchSpaceFix = text.replace(/( )(?=[^ ]$)/, '&nbsp;')
    $element.html = $element.html.replace(text, textWithFrenchSpaceFix)
}

const updateTemplatedElement = ($element, parameter, variables) => {
    Object.keys(variables).forEach((key) => {
        $element[parameter] = $element[parameter].replaceAll(`{{${key}}}`, variables[key])
    })
}

const backButton = () => {
    if (wixWindow.rendering.env !== 'browser') return

    const $backButton = $w('#backButton')
    $backButton.link = getPreviousPage()
    $backButton.target = '_self'
    $backButton.enable()
}

// function setAuthorStructuredData(author) {
//     const {
//         name,
//         title,
//         profilePhoto,
//         website,
//         linkedinLink,
//         twitterLink,
//         facebookLink,
//         instagramLink,
//         tiktokLink,
//         youtubeLink
//     } = author

//     const { baseUrl, prefix, path } = wixLocationFrontend
//     const url = `${baseUrl}/${prefix}/${path.join('/')}`

//     const imageData = getImageData(profilePhoto)

//     const sameAs = [
//         website,
//         linkedinLink,
//         twitterLink,
//         facebookLink,
//         instagramLink,
//         tiktokLink,
//         youtubeLink
//     ].filter((link) => link)

//     const authorStructuredData = {
//         "@context": "https://schema.org/",
//         "@type": "Person",
//         "name": name,
//         "url": url,
//         "image": {
//             "@type": "ImageObject",
//             "url": imageData.url,
//             "width": imageData.width,
//             "height": imageData.height
//         },
//         "sameAs": sameAs,
//         "jobTitle": title,
//         "worksFor": {
//             "@type": "Organization",
//             "name": "Wix.com"
//         }
//     }

//     return wixSeoFrontend.setStructuredData([authorStructuredData])
// }

const setAuthorCanonical = (canonical) => {
    if (!canonical || !isValidHttpUrl(canonical)) return

    $w('#pagination').skipCanonical = true

    return wixSeoFrontend
        .setLinks([{
            rel: 'canonical',
            href: canonical
        }])
}

export function setMetaTags(author) {
    const newMetaTags = [...wixSeoFrontend.metaTags]

    const replaceMetaTag = (attribute, value, content) => {
        const existingTagIndex = newMetaTags.findIndex(tag => tag[attribute] === value)
        if (existingTagIndex !== -1) newMetaTags.splice(existingTagIndex, 1)

        if (content) newMetaTags.push({
            [attribute]: value,
            content
        })
    }

    const { seoDescription, description, profilePhoto } = author
    const imageData = getImageData(profilePhoto)

    // wixSeoFrontend.metaTags gets default static tag values instead of actual dynamic values so we need to overwrite these values
    replaceMetaTag('name', 'twitter:card', 'summary')

    const pageTitle = createPageTitle(author)
    replaceMetaTag('property', 'og:title', pageTitle)
    replaceMetaTag('name', 'twitter:title', pageTitle)

    const tagDescription = seoDescription || createSeoDescription(description, 100, 155)
    replaceMetaTag('property', 'og:description', tagDescription)
    replaceMetaTag('name', 'twitter:description', tagDescription)
    replaceMetaTag('name', 'description', tagDescription)

    replaceMetaTag('property', 'og:image', imageData.url)
    replaceMetaTag('name', 'twitter:image', imageData.url)

    replaceMetaTag('property', 'og:image:width', imageData.width)
    replaceMetaTag('property', 'og:image:height', imageData.height)

    return wixSeoFrontend.setMetaTags(newMetaTags)
}

let templateNameHtml = null
let templateNameHtmlP2 = null

const updateName = (name, page) => {
    if (page === 1) {
        // Page 1
        const $name = $w('#name')
        if (!templateNameHtml) templateNameHtml = $name.html
        $name.html = templateNameHtml
            // .replace('{page}', page)
            .replace('{name}', name)

        $w('#authorProfileP1').restore()
        $w('#authorProfileP2').delete()
    } else {
        // Pages 2+
        const $nameP2 = $w('#nameP2')
        if (!templateNameHtmlP2) templateNameHtmlP2 = $nameP2.html
        const { firstName, lastName } = splitName(name)
        $nameP2.html = templateNameHtmlP2
            .replace('{firstName}', firstName)
            .replace('{lastName}', lastName)
            .replace('{page}', page)

        $w('#authorProfileP1').delete()
        $w('#authorProfileP2').restore()
    }
}

const truncatePostsTitles = () => {
    const postsRepeaterId = $w('#posts').uniqueId

    $w('#addCss').css = `
        #${postsRepeaterId} .wixui-rich-text__text {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
    `
}

const splitName = (name) => ({
    firstName: name.split(' ').slice(0, 1).join(' '),
    lastName: name.split(' ').slice(1).join(' ')
})